import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

import { AnnotationTargetTags, AnnotationTargetType } from '../../app/services/annotation';

export interface AnnotationState {
  annotationType: AnnotationType;
  panelVisible: boolean;
  previewTimezone?: string;
  conversationId?: string;
  eventStart?: string; // ISO timestamp
  eventEnd?: string; // ISO timestamp, optional
  previewStartTime?: string; // ISO timestamp, optional
  previewEndTime?: string; // ISO timestamp, optional
  targetType?: AnnotationTargetType;
  target_id: {
    tags?: AnnotationTargetTags[];
    model_id?: number | null;
    ensemble_id?: number | null;
    limit_id?: number | null;
    view_id?: string;
  };
  annotationMode: boolean;
}

interface AnnotationContextProps {
  annotationState: AnnotationState;
  openPanel: (params: Partial<AnnotationState>) => void; // set context & show panel
  closePanel: () => void; // hide panel
  setAnnotationMode: (mode: boolean) => void;
  showPanel: (visibility: boolean) => void;
  resetAnnotations: () => void; // reset all annotation state
  updateAnnotationTags: (tags: AnnotationTargetTags[]) => void; // update annotation tags
}

export enum AnnotationType {
  REMARK = 'REMARK',
  NOTE = 'NOTE',
}

const defaultState: AnnotationState = {
  annotationType: AnnotationType.NOTE,
  panelVisible: false,
  annotationMode: false,
  target_id: {
    tags: undefined,
    model_id: undefined,
    ensemble_id: undefined,
    limit_id: undefined,
    view_id: undefined,
  },
};

const AnnotationContext = createContext<AnnotationContextProps>({
  annotationState: defaultState,
  openPanel: () => null,
  closePanel: () => null,
  setAnnotationMode: () => null,
  showPanel: () => null,
  resetAnnotations: () => null,
  updateAnnotationTags: () => null,
});

export const AnnotationContextProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [annotationState, setAnnotationState] = useState<AnnotationState>(defaultState);

  const openPanel = (params: Partial<AnnotationState>) => {
    setAnnotationState((prev) => ({
      ...prev,
      ...params,
      panelVisible: true,
    }));
  };

  const closePanel = () => {
    setAnnotationState((prev) => ({
      ...prev,
      panelVisible: false,
    }));
  };

  const setAnnotationMode = (mode: boolean) => {
    setAnnotationState((prev) => ({
      ...prev,
      annotationMode: mode,
      // Clear event start time and close panel when annotation mode is disabled
      ...(mode === false && {
        eventStart: undefined,
        eventEnd: undefined,
        panelVisible: false,
      }),
    }));
  };

  const showPanel = (visibility: boolean) => {
    setAnnotationState((prev) => ({
      ...prev,
      panelVisible: visibility,
    }));
  };

  const resetAnnotations = () => {
    setAnnotationState(defaultState);
  };

  const updateAnnotationTags = useCallback((tags: AnnotationTargetTags[]) => {
    setAnnotationState((prev) => ({
      ...prev,
      target_id: {
        ...prev.target_id,
        tags,
      },
    }));
  }, []);

  const value = useMemo(
    () => ({
      annotationState,
      openPanel,
      closePanel,
      setAnnotationMode,
      showPanel,
      resetAnnotations,
      updateAnnotationTags,
    }),
    [annotationState, updateAnnotationTags],
  );

  return <AnnotationContext.Provider value={value}>{children}</AnnotationContext.Provider>;
};

export const useAnnotation = () => {
  const context = useContext(AnnotationContext);
  if (!context) {
    throw new Error('useAnnotation must be used within an AnnotationContextProvider');
  }
  return context;
};
